import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout call failed; clearing the session locally", e);
    }
    setUser(false);
  }, []);

  // stable value, so a re-render of the provider does not re-render every consumer
  const value = useMemo(() => ({ user, setUser, login, logout }), [user, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export const ROLE_LIST = ["admin", "purchase", "warehouse", "dispatch", "billing"];

const SECTIONS = {
  admin: ["dashboard", "schools", "parties", "books", "publishers", "data", "booklists", "stationery", "purchase-orders", "po-hold", "receipts", "claims", "reports", "bundling", "packing", "stock", "samples", "sales-orders", "shipments", "batches", "scan", "pod-ageing", "pod-history", "followups", "tasks", "users", "delete-data", "settings", "publisher-returns", "reallocation", "bills"],
  purchase: ["dashboard", "schools", "parties", "books", "publishers", "data", "booklists", "stationery", "purchase-orders", "po-hold", "sales-orders", "party-pending", "reports", "bundling", "packing", "followups", "tasks", "receipts", "claims", "stock", "pod-ageing", "pod-history", "samples", "publisher-returns", "reallocation", "bills"],
  warehouse: ["dashboard", "books", "publishers", "data", "receipts", "claims", "stock", "samples", "shipments", "scan", "reports", "bundling", "packing", "followups", "tasks", "booklists", "stationery", "pod-ageing", "pod-history", "party-pending", "publisher-returns", "reallocation", "bills"],
  dispatch: ["dashboard", "parties", "sales-orders", "party-pending", "shipments", "batches", "scan", "reports", "bundling", "packing", "followups", "tasks", "stock", "stationery", "pod-ageing", "pod-history", "samples", "publisher-returns", "reallocation", "bills"],
  billing: ["dashboard", "tasks", "sales-orders", "party-pending", "receipts", "stock", "followups", "parties", "pod-history", "publisher-returns", "bills"],
};

/** Every role the user holds — the new list, falling back to the legacy single role. */
export const rolesOf = (user) =>
  (user?.roles?.length ? user.roles : user?.role ? [user.role] : []).filter((r) => ROLE_LIST.includes(r));

/** Access is the union of everything the user's roles allow. */
export const can = (user, section) => {
  const mine = rolesOf(user);
  if (mine.includes("admin")) return true;
  return mine.some((r) => (SECTIONS[r] || []).includes(section));
};
